import { useEffect, useMemo, useState } from 'react';
import {
  ThresholdSliderMetricSection,
  buildPreviewValues,
  coverageMetricConfigs,
} from '@/features/projects/components/metricThresholds';
import { ProjectSettingsAccordion } from '@/features/projects/components/ProjectSettingsAccordion';
import { SettingsPanelActionRow } from '@/features/projects/components/SettingsPanelActionRow';
import {
  defaultProjectMetricThresholds,
  mergeProjectCoverageThresholdGroup,
} from '@/features/projects/utils/projectMetricThresholds';
import { isMetricThresholdDisabled } from '@/shared/utils/metricsThresholds';
import { s } from '@/theme/tokens';
import { Box, Stack } from '@/ui';
import { Button } from '@/ui/primitives/Button';
import type { useUpdateProject } from '@/features/projects';
import type { CoverageMetricThresholdGroup, ProjectDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

type MutationErrorController = { clearError: () => void; setError: (err: unknown) => void };
type NotifyInput = { message: string; tone: 'green' | 'blue' | 'red' };

type CoverageThresholdKey = 'coverageRuns' | 'directories' | 'files';

type ProjectCoverageMetricThresholdSectionProps = {
  title: string;
  description?: string;
  thresholdKey: CoverageThresholdKey;
  successMessage: string;
  project: ProjectDto | null;
  isPending: boolean;
  updateProject: ReturnType<typeof useUpdateProject>;
  mutationError: MutationErrorController;
  notify: (input: NotifyInput) => void;
  defaultExpanded?: boolean;
};

const normaliseRange = (range: number[]): [number, number] => {
  const first = Math.min(100, Math.max(0, range[0] ?? 0));
  const second = Math.min(100, Math.max(0, range[1] ?? first));
  return first <= second ? [first, second] : [second, first];
};

const isDirtyAgainstBaseline = (
  current: CoverageMetricThresholdGroup,
  baseline: CoverageMetricThresholdGroup,
): boolean =>
  coverageMetricConfigs.some(({ thresholdKey }) => {
    const currentMetric = current[thresholdKey];
    const baselineMetric = baseline[thresholdKey];
    return (
      currentMetric.target !== baselineMetric.target ||
      currentMetric.warn !== baselineMetric.warn ||
      currentMetric.enabled !== baselineMetric.enabled
    );
  });

export const ProjectCoverageMetricThresholdSection = ({
  title,
  description,
  thresholdKey,
  successMessage,
  project,
  isPending,
  updateProject,
  mutationError,
  notify,
  defaultExpanded = false,
}: ProjectCoverageMetricThresholdSectionProps): ReactElement => {
  const baselineThresholds = useMemo(
    () =>
      project
        ? mergeProjectCoverageThresholdGroup(
            defaultProjectMetricThresholds[thresholdKey],
            project.metricThresholds[thresholdKey],
          )
        : defaultProjectMetricThresholds[thresholdKey],
    [project, thresholdKey],
  );
  const [drafts, setDrafts] = useState<CoverageMetricThresholdGroup>(baselineThresholds);

  useEffect(() => {
    setDrafts(baselineThresholds);
  }, [baselineThresholds]);

  const isDirty = useMemo(
    () => isDirtyAgainstBaseline(drafts, baselineThresholds),
    [baselineThresholds, drafts],
  );

  return (
    <ProjectSettingsAccordion
      title={title}
      description={description}
      defaultExpanded={defaultExpanded}
    >
      <Stack gap="xl">
        {coverageMetricConfigs.map(({ thresholdKey: metricKey, title: metricTitle }, index) => {
          const metric = drafts[metricKey];
          const metricEnabled = !isMetricThresholdDisabled(metric);
          const previewValues = buildPreviewValues(
            { warn: metric.warn, target: metric.target, direction: 'higherBetter' },
            { min: 0, max: 100, step: 1 },
          );

          return (
            <Stack key={metricKey} gap="md">
              <ThresholdSliderMetricSection
                title={metricTitle}
                enabled={metricEnabled}
                warn={metric.warn}
                target={metric.target}
                formatter={(value) => `${value}%`}
                previewValues={previewValues}
                disabled={!project || isPending}
                onToggle={(checked) => {
                  setDrafts((prev) => ({
                    ...prev,
                    [metricKey]: {
                      ...prev[metricKey],
                      enabled: checked,
                    },
                  }));
                }}
                onChange={(nextWarn, nextTarget) => {
                  const [warn, target] = normaliseRange([nextWarn, nextTarget]);
                  setDrafts((prev) => ({
                    ...prev,
                    [metricKey]: {
                      ...prev[metricKey],
                      warn,
                      target,
                      enabled: true,
                    },
                  }));
                }}
              />
              {index < coverageMetricConfigs.length - 1 ? (
                <Box
                  aria-hidden="true"
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    mt: s('sm'),
                    pt: s('xl'),
                  }}
                />
              ) : null}
            </Stack>
          );
        })}
        <SettingsPanelActionRow>
          <Button
            variant="ghost"
            fullWidthOnMobile={false}
            disabled={!isDirty}
            onClick={() => {
              setDrafts(baselineThresholds);
              mutationError.clearError();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            fullWidthOnMobile={false}
            disabled={!isDirty || isPending || !project}
            onClick={() => {
              if (!project) return;
              updateProject.mutate(
                { id: project.id, metricThresholds: { [thresholdKey]: drafts } },
                {
                  onSuccess: () => {
                    mutationError.clearError();
                    notify({ message: successMessage, tone: 'green' });
                  },
                  onError: (err) => {
                    mutationError.setError(err);
                  },
                },
              );
            }}
          >
            Update
          </Button>
        </SettingsPanelActionRow>
      </Stack>
    </ProjectSettingsAccordion>
  );
};
